import React, { useState } from "react";
import {
  Application,
  downloadApplicationManifest,
} from "@/api/applications";
import { Link } from "react-router-dom";
import { DataGrid, DataGridColumn } from "@/ui-controls/DataGrid";
import MoreActionsButton from "@/ui-controls/MoreActionsButton";
import DeleteApplication from "@/components/applications/DeleteApplication";
import AppCustomers from "@/components/customers/AppCustomers";
import AppRole from "@/components/roles/appRoles/AppRoles";
import AppPrivileges from "@/components/privileges/appPrivileges/AppPrivileges";
import { saveAs } from "file-saver";
import { Configuration } from "@/config";
import UpdateApplication from "@/components/applications/UpdateApplication";

interface Props {
  ApplicationList: Application[];
  onDelete: (app: Application) => any;
  onUpdate?: (app: Application) => any;
}

enum MenuActionType {
  DELETE_APP = 1,
  ADD_CUSTOMER = 2,
  //ADD_USER = 3,
  ADD_ROLE = 4,
  ADD_PRIVILEGE = 5,
  DOWNLOAD_MANIFEST = 6,
  UPDATE_APP = 7,
}

const {
  config: { AUTHORIZATION_APP_ID },
} = new Configuration();

const ApplicationDetailList = (props: Props) => {
  const [selectedApplication, setSelectedApplication] = useState<
    Application | undefined
  >(undefined);
  const [menuActionType, setMenuActionType] = useState<
    MenuActionType | undefined
  >(undefined);
  const countBadgeStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 32,
    height: 28,
    padding: "0 10px",
    borderRadius: 14,
    backgroundColor: "#f3f2f1",
    color: "#323130",
    fontWeight: 600,
    textDecoration: "none",
  };

  const columns: DataGridColumn[] = [
    {
      key: "id",
      name: "Id",
      fieldName: "Id",
      minWidth: 50,
      maxWidth: 60,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "name",
      name: "Name",
      fieldName: "Name",
      minWidth: 150,
      maxWidth: 250,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "client_id",
      name: "B2C Client Id",
      fieldName: "ClientId",
      minWidth: 260,
      maxWidth: 300,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "description",
      name: "Description",
      fieldName: "Description",
      minWidth: 250,
      maxWidth: 350,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "NoCustomers",
      name: "Customers",
      fieldName: "CustomerCount",
      minWidth: 70,
      maxWidth: 100,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
      onRender: (item: Application) => (
        <Link to={`/applications/${item.Id}/customers`} style={countBadgeStyle}>
          {item.CustomersCount}
        </Link>
      ),
    },
    // {
    //   key: "NoUsers",
    //   name: "Users",
    //   fieldName: "UserCount",
    //   minWidth: 70,
    //   maxWidth: 100,
    //   isRowHeader: true,
    //   isResizable: true,
    //   isPadded: true,
    //   onRender: (item: Application) => (
    //     <Link to={`/applications/${item.Id}/users`}>{item.UsersCount}</Link>
    //   ),
    // },
    {
      key: "NoRoles",
      name: "Roles",
      fieldName: "RoleCount",
      minWidth: 70,
      maxWidth: 100,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
      onRender: (item: Application) => (
        <Link to={`/applications/${item.Id}/roles`} style={countBadgeStyle}>
          {item.RolesCount}
        </Link>
      ),
    },
    {
      key: "NoPrivileges",
      name: "Privileges",
      fieldName: "PrivilegeCount",
      minWidth: 70,
      maxWidth: 100,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
      onRender: (item: Application) => (
        <Link
          to={`/applications/${item.Id}/privileges`}
          style={countBadgeStyle}
        >
          {item.PrivilegesCount}
        </Link>
      ),
    },
    {
      key: "Actions",
      name: "",
      minWidth: 50,
      maxWidth: 70,
      onRender: (item: Application) => (
        <MoreActionsButton
          menu={[
            {
              key: "download",
              text: "Download Manifest",
              iconProps: { iconName: "Document" },
              onClick: () => {
                setMenuActionType(MenuActionType.DOWNLOAD_MANIFEST);
                setSelectedApplication(item);
              },
            },
            {
              key: "delete",
              text: "Deactivate application",
              iconProps: { iconName: "Delete" },
              disabled: item.Id === AUTHORIZATION_APP_ID,
              onClick: () => {
                setMenuActionType(MenuActionType.DELETE_APP);
                setSelectedApplication(item);
              },
            },
            {
              key: "update",
              text: "Update application",
              iconProps: { iconName: "Edit" },
              //disabled: item.Id === AUTHORIZATION_APP_ID,
              onClick: () => {
                setMenuActionType(MenuActionType.UPDATE_APP);
                setSelectedApplication(item);
              },
            },
            {
              key: "addSection",
              text: "Add",
              iconProps: { iconName: "Add" },
              subMenuProps: {
                items: [
                  {
                    key: "addCustomer",
                    text: "Customer",
                    title: "Add application customer",
                    onClick: () => {
                      setMenuActionType(MenuActionType.ADD_CUSTOMER);
                      setSelectedApplication(item);
                    },
                  },
                  // {
                  //   key: "addUser",
                  //   text: "User",
                  //   title: "Add application user",
                  //   onClick: () => {
                  //     setMenuActionType(MenuActionType.ADD_USER);
                  //     setSelectedApplication(item);
                  //   },
                  // },
                  {
                    key: "addRole",
                    text: "Role",
                    title: "Add application role",
                    onClick: () => {
                      setMenuActionType(MenuActionType.ADD_ROLE);
                      setSelectedApplication(item);
                    },
                  },
                  {
                    key: "addPrivilege",
                    text: "Privilege",
                    title: "Add application privilege",
                    onClick: () => {
                      setMenuActionType(MenuActionType.ADD_PRIVILEGE);
                      setSelectedApplication(item);
                    },
                  },
                ],
              },
            },
          ]}
        />
      ),
    },
  ];

  const cleanApplicationAction = () => {
    setSelectedApplication(undefined);
    setMenuActionType(undefined);
  };

  const downloadManifest = () => {
    if (selectedApplication) {
      downloadApplicationManifest(selectedApplication.Id).then(
        (result) => {
          const json = JSON.stringify(result);
          let blob = new Blob([json], {
            type: "application/json;charset=utf-8",
          });
          saveAs(blob, `${result.Name.replace(/\s/g, "_")}_Manifest`);
        },
        (error) => {
          // Error handling would go here in real app
        }
      );
    }
  };

  const renderApplicationAction = () => {
    if (!selectedApplication || !menuActionType) return null;

    switch (menuActionType) {
      case MenuActionType.DOWNLOAD_MANIFEST:
        downloadManifest();
        return null;
      case MenuActionType.DELETE_APP:
        return (
          <DeleteApplication
            application={selectedApplication}
            onClose={cleanApplicationAction}
            onDelete={(app) => props.onDelete(app)}
          />
        );
      case MenuActionType.UPDATE_APP:
        return (
          <UpdateApplication
            application={selectedApplication}
            onClose={cleanApplicationAction}
            onUpdate={(app) => {
              if (props.onUpdate) {
                props.onUpdate(app);
              }
            }}
          />
        );
      case MenuActionType.ADD_CUSTOMER:
        return (
          <AppCustomers
            ApplicationId={selectedApplication.Id}
            ShowOnlyNewAppCustomerPanel={true}
            onCloseNewAppCustomerPanel={cleanApplicationAction}
            onSaveAppCustomers={(customers) =>
              (selectedApplication.CustomersCount = customers.length)
            }
          />
        );
      // case MenuActionType.ADD_USER:
      //   return (
      //     <AppUsers
      //       ApplicationId={selectedApplication.Id}
      //       ShowOnlyNewAppUserPanel={true}
      //       onCloseNewAppUserPanel={cleanApplicationAction}
      //       onSaveAppUsers={(users) =>
      //         (selectedApplication.UsersCount = users.length)
      //       }
      //     />
      //   );
      case MenuActionType.ADD_ROLE:
        return (
          <AppRole
            ApplicationId={selectedApplication.Id}
            ShowOnlyNewAppRolePanel={true}
            onCloseNewAppRolePanel={cleanApplicationAction}
            onAddNewRole={(_) => {
              selectedApplication.RolesCount++;
            }}
          />
        );
      case MenuActionType.ADD_PRIVILEGE:
        return (
          <AppPrivileges
            ApplicationId={selectedApplication.Id}
            ShowOnlyNewAppPrivilegePanel={true}
            onCloseNewAppPrivilegePanel={cleanApplicationAction}
            onAddNewPrivilege={(_) => {
              selectedApplication.PrivilegesCount++;
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <DataGrid items={props.ApplicationList} columns={columns} />
      {renderApplicationAction()}
    </>
  );
};

export default ApplicationDetailList;
